import {Shift} from '@domain/shifthandover'
import {FilterDatePicker} from '@hconnect/common/components/dateTimeAndShift/FilterDatePicker'
import {
  DateRange,
  DowntimeStoppageCode,
  ShiftQuickSelectSlots,
  shifthandoverStoppagesList,
  StoppageKind
} from '@hconnect/common/types'
import {Done as DoneIcon} from '@mui/icons-material'
import {Box, Button} from '@mui/material'
import {
  AddFilter,
  CategoryFilterDropDown,
  EquipmentFilters,
  EventTypeFilterDropDown,
  eventAndStoppageCodeTypes,
  PrioritiesFilterDropDown,
  ProcessStageFilter,
  StatusFilterDropDown,
  StoppageCodeFilterDropDown,
  FilterOptions,
  StoppageCodesMap
} from '@widgets/shift-event-filters'
import {intersection, isEmpty} from 'lodash'
import {Moment} from 'moment-timezone'
import React, {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'

export type ShiftSummaryFilterOptionKey =
  | 'processStage'
  | 'equipment'
  | 'mainEquipment'
  | 'eventType'
  | 'category'
  | 'priority'
  | 'status'
  | 'stoppageCode'

export type ShiftSummaryFilterOptions = Pick<
  FilterOptions,
  'timeRange' | ShiftSummaryFilterOptionKey
>

export const shiftSummaryFilterOptions: ShiftSummaryFilterOptionKey[] = [
  'processStage',
  'equipment',
  'mainEquipment',
  'eventType',
  'category',
  'priority',
  'status',
  'stoppageCode'
]

export const defaultShiftSummaryFilters: string[] = ['timeRange']

const getVisibleFilters = ({
  isCollapsible,
  settings
}: {
  isCollapsible: boolean
  settings: ShiftSummaryFilterOptions
}): string[] => {
  if (isCollapsible) {
    return [...defaultShiftSummaryFilters, ...shiftSummaryFilterOptions]
  }
  return Object.keys(settings).reduce(
    (acc, key) => {
      if (settings[key] && defaultShiftSummaryFilters.indexOf(key) === -1) {
        acc.push(key)
      }
      return acc
    },
    [...defaultShiftSummaryFilters]
  )
}

type ShiftSummaryFilterProps = {
  settings: ShiftSummaryFilterOptions
  onChange: (filterOptions: ShiftSummaryFilterOptions) => void
  isCollapsible: boolean
  applyFilter: (filter: ShiftSummaryFilterOptions) => void
  plantId: string
  shifts: Shift[]
  timezone: string
  plantNow: Moment
  kilnStoppageCodes?: DowntimeStoppageCode[]
  rawMillStoppageCodes?: DowntimeStoppageCode[]
  finishMillStoppageCodes?: DowntimeStoppageCode[]
}

export function ShiftSummaryFilter({
  settings,
  onChange,
  isCollapsible,
  applyFilter,
  plantId,
  shifts,
  timezone,
  plantNow,
  kilnStoppageCodes = [],
  rawMillStoppageCodes = [],
  finishMillStoppageCodes = []
}: ShiftSummaryFilterProps) {
  const {
    t,
    i18n: {language}
  } = useTranslation(FEATURE_NAMESPACE)

  const [visibleFilters, setFilter] = useState<string[]>(
    getVisibleFilters({isCollapsible, settings})
  )

  const handleTimeRangeChange = (value: ShiftQuickSelectSlots | DateRange) => {
    const next = {...settings, timeRange: value}
    if (isCollapsible) {
      onChange(next)
    } else {
      applyFilter(next)
    }
  }

  const removeFilter = (filterName: string) => {
    onChange({...settings, [filterName]: undefined})
    setFilter((prevFilters) => prevFilters.filter((pf) => pf !== filterName))
  }

  const selectAll = (filterName: string, filterOptions: unknown[]) => {
    onChange({...settings, [filterName]: filterOptions})
  }

  const allCodesMap = useMemo<StoppageCodesMap<DowntimeStoppageCode>>(
    () => ({
      kiln: kilnStoppageCodes,
      rawMill: rawMillStoppageCodes,
      cementMill: finishMillStoppageCodes
    }),
    [kilnStoppageCodes, rawMillStoppageCodes, finishMillStoppageCodes]
  )

  return (
    <Box
      data-test-id={'shift-summary-filter-options'}
      display={'flex'}
      sx={
        isCollapsible
          ? {
              flexDirection: 'column',
              paddingTop: '30px'
            }
          : {
              flexWrap: 'wrap',
              alignItems: 'center'
            }
      }
    >
      {visibleFilters.includes('timeRange') && (
        <FilterDatePicker
          onChange={handleTimeRangeChange}
          value={settings.timeRange}
          shifts={shifts}
          timezone={timezone}
          plantNow={plantNow}
          language={language}
          translate={t}
          defaultTimeRange="currentShift"
        />
      )}

      {visibleFilters.includes('processStage') && (
        <ProcessStageFilter
          onChange={(processStage) => onChange({...settings, processStage})}
          removeFilter={removeFilter}
          value={settings.processStage}
          selectAll={selectAll}
          isCollapsible={isCollapsible}
          plantId={plantId}
        />
      )}

      <EquipmentFilters
        visibleFilters={visibleFilters}
        removeFilter={removeFilter}
        plantId={plantId}
        equipment={settings.equipment}
        onEquipmentChange={(equipment) => onChange({...settings, equipment})}
        mainEquipment={settings.mainEquipment}
        onMainEquipmentChange={(mainEquipment) => onChange({...settings, mainEquipment})}
      />

      {visibleFilters.includes('eventType') && (
        <EventTypeFilterDropDown
          data-test-id={'shift-summary-filter-eventType'}
          onChange={(value) => onChange({...settings, eventType: value, stoppageCode: undefined})}
          value={settings.eventType}
          removeFilter={removeFilter}
          label={t('shiftEvent.label.eventType')}
          isCollapsible={isCollapsible}
          options={eventAndStoppageCodeTypes}
          name="eventType"
          selectAll={(selectedOptions) => {
            onChange({...settings, eventType: selectedOptions, stoppageCode: undefined})
          }}
        />
      )}

      {visibleFilters.includes('category') && (
        <CategoryFilterDropDown
          data-test-id={'shift-summary-filter-category'}
          onChange={(value) => onChange({...settings, category: value})}
          value={settings.category}
          removeFilter={removeFilter}
          isCollapsible={isCollapsible}
          selectAll={selectAll}
        />
      )}

      {visibleFilters.includes('priority') && (
        <PrioritiesFilterDropDown
          data-test-id={'shift-summary-filter-priority'}
          onChange={(value) => onChange({...settings, priority: value})}
          value={settings.priority}
          removeFilter={removeFilter}
          isCollapsible={isCollapsible}
          selectAll={selectAll}
        />
      )}

      {visibleFilters.includes('status') && (
        <StatusFilterDropDown
          data-test-id={'shift-summary-filter-status'}
          onChange={(value) => onChange({...settings, status: value})}
          value={settings.status}
          removeFilter={removeFilter}
          isCollapsible={isCollapsible}
          selectAll={selectAll}
        />
      )}

      {visibleFilters.includes('stoppageCode') && (
        <StoppageCodeFilterDropDown
          data-test-id={'shift-summary-filter-stoppageCode'}
          onChange={(value) => {
            onChange({...settings, stoppageCode: value})
          }}
          value={settings.stoppageCode}
          stoppageKinds={
            intersection(settings.eventType, shifthandoverStoppagesList) as StoppageKind[]
          }
          stoppageCodesMap={allCodesMap}
        />
      )}

      {!isCollapsible && !isEmpty(shiftSummaryFilterOptions) && (
        <AddFilter<ShiftSummaryFilterOptionKey>
          data-test-id={'shift-summary-add-filter'}
          options={shiftSummaryFilterOptions}
          translate={t}
          onChange={(value: string[] = []) => {
            return setFilter((prevFilters) => {
              const filterIntersection = prevFilters.filter((x: string) => !value.includes(x))
              const filterName = filterIntersection[0] as keyof ShiftSummaryFilterOptions
              onChange({...settings, [filterName]: undefined})
              return value
            })
          }}
          values={visibleFilters}
        />
      )}

      {isCollapsible && (
        <Box m={1}>
          <Button
            fullWidth
            data-test-id={'shift-summary-apply-filter'}
            onClick={() => applyFilter(settings)}
            variant="contained"
            color="primary"
            startIcon={<DoneIcon />}
          >
            {t('shiftEvent.label.applyFilter')}
          </Button>
        </Box>
      )}
    </Box>
  )
}
